import { Visibility, VisibilityOff } from '@mui/icons-material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  EMAIL_CHAR_LIMIT,
  NAME_CHAR_LIMIT,
  PASSWORD_CHAR_LIMIT,
} from '../../../../../corelogic/domain/constants/fieldLimits.constants';
import {
  validateForm,
  validateFormFieldSignUp,
} from '../../../../../corelogic/domain/utils/formValidationSignUp.utils';
import { ValidationResult } from '../../../../../corelogic/domain/utils/realtimeSecurityValidation.utils';
import { emailSignUp } from '../../../../../corelogic/state/authentication/authentication.action';
import { store } from '../../../../../corelogic/state/store';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useUserProfile } from '../../hooks/useUserProfile';
import { CountrySelector } from '../Shared/CountrySelector';
import { SecureTextField } from '../Shared/SecureTextField/SecureTextField';

const styles = {
  container: {
    minHeight: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    pt: 1,
    pb: 1,
    backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.9)), url('/viewer_octagon.png')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    flex: 1,
    position: 'relative',
    overflow: 'auto',
  },
  signupBox: {
    width: '100%',
    maxWidth: '450px',
    height: 'auto',
    padding: '10px',
    borderRadius: '8px',
    backgroundColor: 'rgba(20, 20, 20, 0.9)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.7)',
    textAlign: 'center',
  },
  logo: {
    maxWidth: '150px',
    marginBottom: '20px',
    filter: 'drop-shadow(0 2px 5px rgba(0, 0, 0, 0.5))',
  },
  title: {
    fontFamily: 'Rubik Mono One',
    color: '#ffffff',
    fontSize: 'clamp(1.3rem, 2vw, 1.8rem)',
    textTransform: 'uppercase',
  },
  divider: {
    color: 'rgba(255, 255, 255, 0.5)',
    margin: '15px 0',
    width: '100%',
    fontFamily: 'Rubik Mono One',
    fontSize: '0.8rem',
    '&::before, &::after': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
  },
  input: {
    marginBottom: '15px',
    width: '100%',
    '& .MuiInputBase-input': {
      color: 'white',
      height: {
        xs: '1.4rem',
        sm: 'auto',
      },
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      },
      '&:hover fieldset': {
        borderColor: '#cc0000',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#cc0000',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
      '&.Mui-focused': {
        color: '#8B0000',
      },
    },
    '& .MuiInputAdornment-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    '& .MuiIconButton-root': {
      color: 'rgba(255, 255, 255, 0.7)',
      '&:hover': {
        color: 'white',
      },
    },
  },
  signupButton: {
    backgroundColor: '#8B0000',
    color: 'white',
    fontWeight: 'bold',
    padding: '10px 0',
    marginTop: '10px',
    marginBottom: '15px',
    width: '100%',
    '&:hover': {
      backgroundColor: '#aa0000',
    },
    '&.Mui-disabled': {
      backgroundColor: 'rgba(139, 0, 0, 0.5)',
      color: 'rgba(255, 255, 255, 0.5)',
    },
  },
  loginText: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: '0.9rem',
    marginTop: '10px',
  },
  loginLink: {
    color: '#cc0000',
    textDecoration: 'none',
    fontWeight: 'bold',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  formField: {
    marginBottom: '15px',
    '& .MuiFormLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
    },
    '& .MuiSelect-select': {
      color: 'white',
      textAlign: 'left',
    },
    '& .MuiOutlinedInput-root': {
      color: 'white',
      height: {
        xs: '40px',
        sm: 'auto',
      },
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.5)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#8B0000',
      },
    },
  },
};

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  dateOfBirth?: string;
}

const SignUp: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    nationality: '',
    dateOfBirth: '',
    gender: '',
  });

  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false,
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [fieldValidations, setFieldValidations] = useState<
    Record<string, ValidationResult>
  >({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  const { isAuthenticated, isLoading: authLoading } = useAuthentication();
  const { isLoading: userLoading } = useUserProfile();
  const isLoading = isSubmitting || authLoading || userLoading || false;

  // Redirect if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const togglePasswordVisibility = (field: 'password' | 'confirmPassword') => {
    setShowPassword({
      ...showPassword,
      [field]: !showPassword[field],
    });
  };

  const handleSecureChange = (
    fieldName: string,
    value: string,
    validation: ValidationResult,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    // Store the validation result for this field
    setFieldValidations((prev) => ({
      ...prev,
      [fieldName]: validation,
    }));

    // Clear any existing form errors for this field
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[fieldName as keyof FormErrors];
      return newErrors;
    });
  };

  // Regular change handler for non-secure fields
  const handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
      | SelectChangeEvent,
  ) => {
    const { name, value } = event.target;

    if (!name) return;

    // Update form data
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Validate the field
    const error = validateFormFieldSignUp(name, value as string, formData);

    // Update errors
    setFormErrors((prev) => {
      if (error) {
        return { ...prev, [name]: error };
      } else {
        const newErrors = { ...prev };
        delete newErrors[name as keyof typeof formErrors];
        return newErrors;
      }
    });
  };

  const handleCountryChange = (code: string) => {
    setFormData({
      ...formData,
      nationality: code,
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && event.target === document.activeElement) {
      // Prevent form submission if Country selector is focused
      if ((event.target as HTMLElement).getAttribute('role') === 'combobox') {
        event.preventDefault();
      }
    }
  };

  // Check if there are any high-severity security issues
  const hasSecurityIssues = () => {
    return Object.values(fieldValidations).some((validation) =>
      validation.warnings.some((warning) => warning.severity === 'high'),
    );
  };

  // Pre-submit handling - now prevents submission if there are security issues
  const handlePreSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);

    // Validate all fields first
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setIsSubmitting(false);
      return;
    }

    // Check for high-severity security issues
    if (hasSecurityIssues()) {
      setIsSubmitting(false);
      // Don't show modal - security messages are already displayed below fields
      return;
    }

    // Proceed with submission
    handleFinalSubmit();
  };

  // Final submission - no more security dialog
  const handleFinalSubmit = () => {
    store.dispatch(
      emailSignUp({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        confirmPassword: formData.confirmPassword,
        nationality: formData.nationality || undefined,
        dateOfBirth: formData.dateOfBirth || undefined,
        gender: formData.gender || undefined,
        retry: 0,
      }),
    );
    setIsSubmitting(false);
  };

  const isFormValid = () => {
    const requiredFields = [
      'firstName',
      'lastName',
      'email',
      'password',
      'confirmPassword',
    ];
    const hasAllRequiredFields = requiredFields.every(
      (field) => !!formData[field as keyof typeof formData],
    );
    return (
      hasAllRequiredFields &&
      Object.keys(formErrors).length === 0 &&
      !hasSecurityIssues()
    );
  };

  // Check if password meets requirements
  const isPasswordValid = formData.password.length >= 6;

  // Framer Motion Container
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <Box sx={styles.container}>
      <motion.div variants={container} initial="hidden" animate="show">
        <Paper elevation={0} sx={styles.signupBox}>
          <img
            src="/fantasy_mma_logo.png"
            alt="Fantasy MMA Logo"
            style={styles.logo}
          />

          <Typography variant="h4" sx={styles.title}>
            Create Account
          </Typography>
          <Divider sx={styles.divider}>SIGN UP</Divider>

          <form onSubmit={handlePreSubmit} onKeyDown={handleKeyDown}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <SecureTextField
                name="firstName"
                label="First Name"
                variant="outlined"
                fullWidth
                value={formData.firstName}
                onChange={(value, validation) =>
                  handleSecureChange('firstName', value, validation)
                }
                fieldType="name"
                fieldName="First Name"
                error={!!formErrors.firstName}
                helperText={formErrors.firstName}
                required
                showCharacterCounter={true}
                inputProps={{ maxLength: NAME_CHAR_LIMIT }}
                sx={styles.input}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <SecureTextField
                name="lastName"
                label="Last Name"
                variant="outlined"
                fullWidth
                value={formData.lastName}
                onChange={(value, validation) =>
                  handleSecureChange('lastName', value, validation)
                }
                fieldType="name"
                fieldName="Last Name"
                error={!!formErrors.lastName}
                helperText={formErrors.lastName}
                required
                showCharacterCounter={true}
                inputProps={{ maxLength: NAME_CHAR_LIMIT }}
                sx={styles.input}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <SecureTextField
              name="email"
              label="Email"
              variant="outlined"
              type="email"
              fullWidth
              value={formData.email}
              onChange={(value, validation) =>
                handleSecureChange('email', value, validation)
              }
              fieldType="email"
              fieldName="Email"
              error={!!formErrors.email}
              helperText={formErrors.email}
              required
              showCharacterCounter={true}
              inputProps={{ maxLength: EMAIL_CHAR_LIMIT }}
              sx={styles.input}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Divider sx={styles.divider}>PERSONAL INFORMATION</Divider>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                name="dateOfBirth"
                label="Date of Birth"
                type="date"
                fullWidth
                value={formData.dateOfBirth}
                onChange={handleChange}
                error={!!formErrors.dateOfBirth}
                helperText={formErrors.dateOfBirth}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  max: new Date().toISOString().split('T')[0],
                }}
                sx={styles.formField}
              />

              <FormControl fullWidth sx={styles.formField}>
                <InputLabel>Gender</InputLabel>
                <Select
                  name="gender"
                  value={formData.gender}
                  label="Gender"
                  onChange={handleChange}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: '#1e1e1e',
                        color: 'white',
                      },
                    },
                  }}
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                  <MenuItem value="prefer_not_to_say">
                    Prefer not to say
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            <CountrySelector
              value={formData.nationality}
              onChange={handleCountryChange}
              sx={{ mb: 2 }}
            />

            <Divider sx={styles.divider}>PASSWORD</Divider>

            <TextField
              name="password"
              label="Password"
              variant="outlined"
              type={showPassword.password ? 'text' : 'password'}
              fullWidth
              value={formData.password}
              onChange={handleChange}
              error={!!formErrors.password}
              helperText={
                formErrors.password || (
                  <span
                    style={{ color: isPasswordValid ? '#4caf50' : 'inherit' }}
                  >
                    {isPasswordValid ? '✓ ' : ''}Password must be at least 6
                    characters
                  </span>
                )
              }
              required
              inputProps={{ maxLength: PASSWORD_CHAR_LIMIT }}
              sx={styles.input}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => togglePasswordVisibility('password')}
                      edge="end"
                    >
                      {showPassword.password ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              name="confirmPassword"
              label="Confirm Password"
              variant="outlined"
              type={showPassword.confirmPassword ? 'text' : 'password'}
              fullWidth
              value={formData.confirmPassword}
              onChange={handleChange}
              error={!!formErrors.confirmPassword}
              helperText={
                formErrors.confirmPassword ||
                (formData.confirmPassword &&
                formData.confirmPassword === formData.password ? (
                  <span style={{ color: '#4caf50' }}>✓ Passwords match</span>
                ) : (
                  ''
                ))
              }
              required
              inputProps={{ maxLength: PASSWORD_CHAR_LIMIT }}
              sx={styles.input}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        togglePasswordVisibility('confirmPassword')
                      }
                      edge="end"
                    >
                      {showPassword.confirmPassword ? (
                        <VisibilityOff />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              sx={styles.signupButton}
              disabled={isLoading || !isFormValid()}
            >
              {isLoading ? 'Please wait...' : 'Create Account'}
            </Button>
          </form>

          <Typography sx={styles.loginText}>
            Already have an account?{' '}
            <Link to="/login" style={styles.loginLink}>
              Sign in here
            </Link>
          </Typography>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default SignUp;
